import React, { useState } from 'react';
import { Phone, CheckCircle, Car, TrendingUp, Award, Wrench, ChevronDown, Instagram, Facebook, Music } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Home = () => {
  const whatsappLink = "https://api.whatsapp.com/send/?phone=5516981404507&text=Ol%C3%A1%21+Gostaria+de+saber+mais+sobre+o+servi%C3%A7o+de+Placa+Preta+para+meu+ve%C3%ADculo+cl%C3%A1ssico.&type=phone_number&app_absent=0";

  const handleWhatsAppClick = (buttonLocation) => {
    // Disparar evento no GTM dataLayer para rastreamento
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'whatsapp_click',
        'button_location': buttonLocation,
        'event_category': 'contact',
        'event_action': 'click_whatsapp',
        'event_label': buttonLocation
      });
    }
    
    // Aguardar um momento para garantir que o evento foi registrado
    setTimeout(() => {
      window.open(whatsappLink, '_blank');
    }, 300);
  };

  const scrollToProcess = () => {
    // Disparar evento no GTM para botão de avaliação
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'avaliacao_gratuita_click',
        'button_location': 'hero_section',
        'event_category': 'conversion',
        'event_action': 'click_avaliacao_gratuita',
        'event_label': 'hero_cta_secondary'
      });
    }
    
    setTimeout(() => {
      window.open(whatsappLink, '_blank');
    }, 300);
  };

  const benefits = [
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: "Valorização do veículo",
      description: "Carros com Placa Preta são mais valorizados no mercado."
    },
    {
      icon: <Award className="w-12 h-12" />,
      title: "Reconhecimento oficial de veículo de coleção",
      description: "Seu carro passa a ter certificação histórica."
    },
    {
      icon: <Wrench className="w-12 h-12" />,
      title: "Processo orientado por especialistas",
      description: "Acompanhamento completo para facilitar toda a aprovação."
    }
  ];

  const requirements = [
    "Possui mais de 30 anos de fabricação",
    "Está bem conservado ou restaurado",
    "Mantém características originais",
    "Pode passar por avaliação técnica"
  ];

  const processSteps = [
    {
      number: "01",
      title: "Análise inicial do veículo",
      description: "Verificamos se seu carro pode se enquadrar."
    },
    {
      number: "02",
      title: "Orientação técnica",
      description: "Indicamos ajustes necessários para aprovação."
    },
    {
      number: "03",
      title: "Avaliação e certificação",
      description: "Seu veículo passa pelo processo oficial."
    },
    {
      number: "04",
      title: "Emissão da Placa Preta",
      description: "Carro reconhecido como veículo de coleção."
    }
  ];

  const targetAudience = [
    "Colecionadores de carros antigos",
    "Proprietários de veículos clássicos",
    "Quem deseja valorizar seu automóvel"
  ];

  const faqs = [
    {
      question: "Qualquer carro antigo pode ter Placa Preta?",
      answer: "Não necessariamente. O veículo precisa ter mais de 30 anos de fabricação, estar bem conservado e manter suas características originais. Nossa equipe faz uma análise inicial para verificar se seu carro se enquadra nos requisitos do CONTRAN."
    },
    {
      question: "Quanto tempo leva o processo?",
      answer: "O processo de análise e emissão pode ser concluído em até 24 horas, dependendo da documentação do veículo. Nós acompanhamos cada etapa para garantir agilidade máxima no atendimento."
    },
    {
      question: "Preciso restaurar o carro completamente?",
      answer: "Não é necessário uma restauração completa, mas o veículo precisa estar em bom estado de conservação e manter suas características originais. Orientamos sobre eventuais ajustes necessários para aprovação na vistoria."
    },
    {
      question: "Vocês atendem toda São Paulo?",
      answer: "Sim, atendemos TODO O BRASIL! Nossa consultoria é especializada no processo de obtenção de Placa Preta para veículos em qualquer estado do país. Entre em contato conosco pelo WhatsApp e realize seu processo de forma rápida e eficiente, não importa onde você esteja."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Floating WhatsApp Button */}
      <button
        onClick={() => handleWhatsAppClick('floating_button')}
        className="fixed left-6 bottom-6 z-50 bg-[#25D366] hover:bg-[#20BA5A] text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 group"
        aria-label="Falar no WhatsApp"
      >
        <Phone className="w-6 h-6" />
        <span className="absolute left-full ml-3 bg-[#0a0a0a] text-[#FFD700] px-4 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-semibold">
          Fale Conosco
        </span>
      </button>

      {/* Header/Navigation */}
      <header className="fixed top-0 w-full bg-[#0a0a0a]/95 backdrop-blur-md z-50 border-b border-[#FFD700]/20">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/3sqdjwfs_ChatGPT%20Image%207%20de%20mar.%20de%202026%2C%2007_12_52.png" 
                alt="Placa Preta Coleção" 
                className="h-24 w-auto"
              />
            </div>
            
            {/* Navigation Menu */}
            <div className="hidden lg:flex items-center gap-8">
              <a 
                href="#inicio" 
                className="text-[#e5e5e5] hover:text-[#FFD700] transition-colors duration-300 font-medium"
              >
                Início
              </a>
              <a 
                href="#beneficios" 
                className="text-[#e5e5e5] hover:text-[#FFD700] transition-colors duration-300 font-medium"
              >
                Benefícios
              </a>
              <a 
                href="#como-funciona" 
                className="text-[#e5e5e5] hover:text-[#FFD700] transition-colors duration-300 font-medium"
              >
                Como Funciona
              </a>
              <a 
                href="#veiculos" 
                className="text-[#e5e5e5] hover:text-[#FFD700] transition-colors duration-300 font-medium"
              >
                Veículos
              </a>
              <a 
                href="#faq" 
                className="text-[#e5e5e5] hover:text-[#FFD700] transition-colors duration-300 font-medium"
              >
                FAQ
              </a>
            </div>
            
            <div className="flex items-center gap-6">
              <a href="https://www.instagram.com/luizaraujo2012" target="_blank" rel="noopener noreferrer" className="text-[#FFD700] hover:text-[#FFC700] transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/luiz.c.araujo.98" target="_blank" rel="noopener noreferrer" className="text-[#FFD700] hover:text-[#FFC700] transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.tiktok.com/@luizcarlosaraujo91" target="_blank" rel="noopener noreferrer" className="text-[#FFD700] hover:text-[#FFC700] transition-colors">
                <Music className="w-5 h-5" />
              </a>
              <Button 
                onClick={() => handleWhatsAppClick('header_button')}
                className="bg-[#FFD700] text-[#0a0a0a] hover:bg-[#FFC700] font-semibold"
              >
                <Phone className="w-4 h-4 mr-2" />
                Contato
              </Button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="inicio" className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden pt-24 md:pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/a89ytt54_567533343_24798548386493315_7333230007609101587_n.jpg"
            alt="Carro Clássico"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/90 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            {/* Badges de Destaque */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="bg-[#FFD700] text-[#0a0a0a] px-4 py-2 rounded-full font-bold text-sm md:text-base lg:text-lg flex items-center gap-2 shadow-xl">
                <CheckCircle className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6" />
                Atendemos Todo Brasil
              </div>
              <div className="bg-[#FFD700] text-[#0a0a0a] px-4 py-2 rounded-full font-bold text-sm md:text-base lg:text-lg flex items-center gap-2 shadow-xl">
                <Award className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6" />
                Processo em até 24h
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#FFD700] mb-4 md:mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Seu carro antigo pode conquistar a Placa Preta
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-[#e5e5e5] mb-6 md:mb-8 leading-relaxed">
              Seu carro merece reconhecimento: faça a certificação de Placa Preta.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <Button 
                onClick={() => handleWhatsAppClick('hero_primary_button')}
                className="bg-[#FFD700] text-[#0a0a0a] hover:bg-[#FFC700] font-semibold text-base md:text-lg px-6 py-5 md:px-8 md:py-6 w-full sm:w-auto"
              >
                <Phone className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                Falar no WhatsApp
              </Button>
              <Button 
                onClick={scrollToProcess}
                variant="outline"
                className="border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700] hover:text-[#0a0a0a] font-semibold text-base md:text-lg px-6 py-5 md:px-8 md:py-6 w-full sm:w-auto"
              >
                Avaliação gratuita
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-[#FFD700]" />
        </div>
      </section>

      {/* Highlight Section - Atendimento Nacional */}
      <section className="py-16 bg-gradient-to-r from-[#FFD700] to-[#FFC700] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,.1) 10px, rgba(0,0,0,.1) 20px)' }}></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-[#0a0a0a] p-8 rounded-2xl border-4 border-[#0a0a0a] shadow-2xl text-center transform hover:scale-105 transition-transform duration-300">
              <div className="bg-[#FFD700] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-[#0a0a0a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-[#FFD700] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Atendimento Nacional
              </h3>
              <p className="text-xl text-[#e5e5e5] leading-relaxed">
                Prestamos serviço em <span className="text-[#FFD700] font-bold">todo o Brasil</span>, não importa onde você esteja!
              </p>
            </div>

            <div className="bg-[#0a0a0a] p-8 rounded-2xl border-4 border-[#0a0a0a] shadow-2xl text-center transform hover:scale-105 transition-transform duration-300">
              <div className="bg-[#FFD700] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-[#0a0a0a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-[#FFD700] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Rapidez Garantida
              </h3>
              <p className="text-xl text-[#e5e5e5] leading-relaxed">
                Processo completo em <span className="text-[#FFD700] font-bold">até 24 horas</span> com toda documentação!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="py-20 bg-[#141414]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#FFD700] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Benefícios da Placa Preta
            </h2>
            <p className="text-xl text-[#8B8D8E]">Transforme seu veículo clássico em uma peça de coleção oficial</p>
          </div>

          {/* Vídeo de Destaque */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="relative rounded-2xl overflow-hidden border-4 border-[#FFD700] shadow-2xl">
              <video 
                controls 
                className="w-full aspect-video bg-black"
                poster="https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/tlexl2m1_567533343_24798548386493315_7333230007609101587_n.jpg"
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/rgs6yj61_WhatsApp%20Video%202026-03-15%20at%2010.10.34.mp4" 
                  type="video/mp4" 
                />
                Seu navegador não suporta vídeos HTML5.
              </video>
              <div className="bg-[#1a1a1a] p-4 border-t-2 border-[#FFD700]">
                <h3 className="text-white font-bold text-lg text-center">Carros clássicos que merecem reconhecimento</h3>
                <p className="text-[#8B8D8E] text-sm text-center">Descubra a beleza e o valor dos veículos de coleção</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="bg-[#1a1a1a] border-[#FFD700]/30 p-8 hover:border-[#FFD700] transition-all duration-300 hover:transform hover:scale-105">
                <div className="text-[#FFD700] mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-[#8B8D8E]">{benefit.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-[#FFD700] mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              Quem pode obter Placa Preta
            </h2>
            <p className="text-xl text-[#e5e5e5] mb-12 text-center">
              Seu veículo pode se qualificar se:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {requirements.map((requirement, index) => (
                <div key={index} className="flex items-start gap-4 bg-[#1a1a1a] p-6 rounded-lg border border-[#FFD700]/30 hover:border-[#FFD700] transition-all duration-300">
                  <CheckCircle className="w-6 h-6 text-[#FFD700] flex-shrink-0 mt-1" />
                  <p className="text-lg text-[#e5e5e5]">{requirement}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="como-funciona" className="py-20 bg-[#141414]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#FFD700] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Como funciona o processo
            </h2>
            <p className="text-xl text-[#8B8D8E]">Um processo simples e orientado por especialistas</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-[#1a1a1a] border border-[#FFD700]/30 p-8 rounded-lg hover:border-[#FFD700] transition-all duration-300 h-full">
                  <div className="text-6xl font-bold text-[#FFD700]/20 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-[#8B8D8E]">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-[#FFD700]/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-[#FFD700] mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>
              Para quem é o serviço
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {targetAudience.map((audience, index) => (
                <div key={index} className="flex items-center gap-4 bg-[#1a1a1a] p-6 rounded-lg border border-[#FFD700]/30 hover:border-[#FFD700] transition-all duration-300">
                  <CheckCircle className="w-6 h-6 text-[#FFD700] flex-shrink-0" />
                  <p className="text-lg text-[#e5e5e5] text-left">{audience}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="veiculos" className="py-20 bg-[#141414]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#FFD700] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Veículos que Atendemos
            </h2>
            <p className="text-xl text-[#8B8D8E]">Exemplos de veículos clássicos e de coleção</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {[
              {
                src: "https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/tlexl2m1_567533343_24798548386493315_7333230007609101587_n.jpg",
                alt: "Mercedes-Benz clássico verde"
              },
              {
                src: "https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/gsaetm6d_566216126_24805450732469747_6580085779715468861_n.jpg",
                alt: "Fuscas clássicos amarelo e verde com Placa Preta"
              },
              {
                src: "https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/fmgrahef_550956858_24565077553173734_6778613494473453850_n.jpg",
                alt: "Volkswagen Gol prata clássico"
              },
              {
                src: "https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/t7zcdtr9_WhatsApp%20Image%202026-03-05%20at%2014.09.58.jpeg",
                alt: "Chevrolet Kadett GL prata com Placa Preta"
              },
              {
                src: "https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/a7bfoqje_WhatsApp%20Image%202026-03-05%20at%2016.23.01-2.jpeg",
                alt: "Jeep laranja vintage restaurado"
              },
              {
                src: "https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/boqob9cv_WhatsApp%20Image%202026-03-05%20at%2014.10.13-2.jpeg",
                alt: "Moto clássica vintage"
              },
              {
                src: "https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/rionwtea_WhatsApp%20Image%202026-03-05%20at%2014.10.13.jpeg",
                alt: "Motocicleta vintage restaurada"
              },
              {
                src: "https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/68xyfopj_WhatsApp%20Image%202026-03-05%20at%2014.09.59.jpeg",
                alt: "Fusca verde clássico"
              },
              {
                src: "https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/3r67gzie_WhatsApp%20Image%202026-03-05%20at%2014.10.03.jpeg",
                alt: "Fusca dourado restaurado"
              }
            ].map((image, index) => (
              <div 
                key={index} 
                className="group relative overflow-hidden rounded-lg border-2 border-[#FFD700]/30 hover:border-[#FFD700] transition-all duration-300 aspect-square"
              >
                <img 
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/90 via-[#0a0a0a]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <div className="p-6 w-full">
                    <p className="text-[#FFD700] font-semibold text-lg">Veículo de Coleção</p>
                    <p className="text-[#e5e5e5] text-sm">Elegível para Placa Preta</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={() => handleWhatsAppClick('gallery_section')}
              className="bg-[#FFD700] text-[#0a0a0a] hover:bg-[#FFC700] font-semibold text-lg px-8 py-6"
            >
              <Phone className="w-5 h-5 mr-2" />
              Consultar meu veículo
            </Button>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-b from-[#FFD700] to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-[#FFD700] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Conheça Nosso Serviço
              </h2>
              <p className="text-xl text-[#8B8D8E]">
                Entenda como funciona o processo de Placa Preta para seu veículo clássico
              </p>
            </div>

            {/* Grid de 2 vídeos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              {/* Vídeo 1 */}
              <div className="relative rounded-2xl overflow-hidden border-4 border-[#FFD700] shadow-2xl">
                <video 
                  controls 
                  className="w-full aspect-video bg-black"
                  poster="https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/exv4u6vg_WhatsApp%20Image%202026-03-05%20at%2014.07.45.jpeg"
                >
                  <source 
                    src="https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/rug9gq68_WhatsApp%20Video%202026-03-05%20at%2014.07.48.mp4" 
                    type="video/mp4" 
                  />
                  Seu navegador não suporta vídeos HTML5.
                </video>
                <div className="bg-[#1a1a1a] p-4 border-t-2 border-[#FFD700]">
                  <h3 className="text-white font-bold text-lg">Luiz explica o serviço</h3>
                  <p className="text-[#8B8D8E] text-sm">Avaliação gratuita e certificação de originalidade</p>
                </div>
              </div>

              {/* Vídeo 2 */}
              <div className="relative rounded-2xl overflow-hidden border-4 border-[#FFD700] shadow-2xl">
                <video 
                  controls 
                  className="w-full aspect-video bg-black"
                  poster="https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/tlexl2m1_567533343_24798548386493315_7333230007609101587_n.jpg"
                >
                  <source 
                    src="https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/aot18i1j_WhatsApp%20Video%202026-03-05%20at%2014.07.46.mp4" 
                    type="video/mp4" 
                  />
                  Seu navegador não suporta vídeos HTML5.
                </video>
                <div className="bg-[#1a1a1a] p-4 border-t-2 border-[#FFD700]">
                  <h3 className="text-white font-bold text-lg">Benefícios da Placa Preta</h3>
                  <p className="text-[#8B8D8E] text-sm">Isenção de IPVA e valorização do seu patrimônio</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#1a1a1a] p-6 rounded-lg border border-[#FFD700]/30 text-center">
                <Award className="w-12 h-12 text-[#FFD700] mx-auto mb-4" />
                <h3 className="text-white font-bold text-lg mb-2">Especialista Certificado</h3>
                <p className="text-[#8B8D8E]">Experiência comprovada em veículos de coleção</p>
              </div>
              
              <div className="bg-[#1a1a1a] p-6 rounded-lg border border-[#FFD700]/30 text-center">
                <CheckCircle className="w-12 h-12 text-[#FFD700] mx-auto mb-4" />
                <h3 className="text-white font-bold text-lg mb-2">Avaliação Gratuita</h3>
                <p className="text-[#8B8D8E]">Análise inicial sem compromisso</p>
              </div>
              
              <div className="bg-[#1a1a1a] p-6 rounded-lg border border-[#FFD700]/30 text-center">
                <Phone className="w-12 h-12 text-[#FFD700] mx-auto mb-4" />
                <h3 className="text-white font-bold text-lg mb-2">Atendimento Direto</h3>
                <p className="text-[#8B8D8E]">Fale direto com o especialista</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#FFD700] to-[#b8941f] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1703875497368-512f045c87f0"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Car className="w-16 h-16 text-[#0a0a0a] mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold text-[#0a0a0a] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Descubra se seu carro pode receber Placa Preta
            </h2>
            <p className="text-xl text-[#0a0a0a]/80 mb-8">
              Fale agora com um especialista e receba uma avaliação inicial do seu veículo.
            </p>
            <Button 
              onClick={() => handleWhatsAppClick('cta_section')}
              className="bg-[#0a0a0a] text-[#FFD700] hover:bg-[#1a1a1a] font-semibold text-lg px-12 py-7 shadow-2xl"
            >
              <Phone className="w-5 h-5 mr-2" />
              Falar no WhatsApp
            </Button>
            <p className="mt-6 text-[#0a0a0a]/70">
              WhatsApp: (16) 98140-4507
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-[#141414]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-[#FFD700] mb-12 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              Perguntas Frequentes
            </h2>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-[#1a1a1a] border border-[#FFD700]/30 rounded-lg px-6 hover:border-[#FFD700] transition-all duration-300"
                >
                  <AccordionTrigger className="text-left text-lg text-white hover:text-[#FFD700] py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#8B8D8E] pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] border-t border-[#FFD700]/20 py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_2fc0c2cc-9db8-4530-ae96-86e76b7d6b2b/artifacts/9fqjmex2_WhatsApp%20Image%202026-03-05%20at%2014.07.45.jpeg" 
                alt="Placa Preta Coleção" 
                className="h-16 w-auto mb-4"
              />
              <p className="text-[#8B8D8E]">
                Especialistas em consultoria para obtenção de Placa Preta em São Paulo.
              </p>
            </div>

            <div>
              <h3 className="text-[#FFD700] font-bold text-xl mb-4">Contato</h3>
              <div className="space-y-2 text-[#8B8D8E]">
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  (16) 98140-4507
                </p>
                <p>Responsável: Luiz</p>
                <p className="text-[#FFD700] font-bold">🇧🇷 Atendimento em todo Brasil</p>
                <p className="text-[#FFD700] font-bold">⚡ Processo em até 24 horas</p>
              </div>
            </div>

            <div>
              <h3 className="text-[#FFD700] font-bold text-xl mb-4">Redes Sociais</h3>
              <div className="flex gap-4">
                <a 
                  href="https://www.instagram.com/luizaraujo2012" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-[#1a1a1a] p-3 rounded-lg hover:bg-[#FFD700] hover:text-[#0a0a0a] transition-all duration-300 text-[#FFD700]"
                >
                  <Instagram className="w-6 h-6" />
                </a>
                <a 
                  href="https://www.facebook.com/luiz.c.araujo.98" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-[#1a1a1a] p-3 rounded-lg hover:bg-[#FFD700] hover:text-[#0a0a0a] transition-all duration-300 text-[#FFD700]"
                >
                  <Facebook className="w-6 h-6" />
                </a>
                <a 
                  href="https://www.tiktok.com/@luizcarlosaraujo91" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-[#1a1a1a] p-3 rounded-lg hover:bg-[#FFD700] hover:text-[#0a0a0a] transition-all duration-300 text-[#FFD700]"
                >
                  <Music className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-[#FFD700]/20 mt-12 pt-8 text-center text-[#8B8D8E]">
            <p>&copy; 2024 Placa Preta Coleção. Todos os direitos reservados.</p>
            <p className="mt-2">www.placapretacolecao.com.br</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
